<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Refund"
                sample-csv-path="/createRefundByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Create Refund by Import </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <p>
                        <master-data-reference-button
                            button-label="Marketplace List"
                            dialog-title="Valid Marketplace Codes"
                            :items="marketplaceOptions"
                        />
                        <master-data-reference-button
                            button-label="Currency List"
                            dialog-title="Valid Currency Codes"
                            :items="currencyOptions"
                        />
                        <master-data-reference-button
                            button-label="Refund Type List"
                            dialog-title="Valid Refund Types"
                            :items="refundTypeList"
                        />
                    </p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListRefund"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { RefundLabelTextHelper } from '../../utils/labelTextHelper/RefundLabelTextHelper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { RefundHeaderHelper } from '../../utils/headerHelper/RefundHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import { useMasterData } from '../../composables/useMasterData';
import MasterDataReferenceButton from '../../components/MasterDataReferenceButton.vue';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { RefundImportValidation } from '../../config/importValidationConfigs';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';

export default defineComponent({
    name: 'ImportRefund',

    components: {
        MasterDataReferenceButton,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        // Load master data options for reference
        const { marketplaceOptions, currencyOptions, loadAllMasterData } = useMasterData();

        // Refund type enum values for reference
        const refundTypeList = [
            { code: 'ORDER_REFUND_BUYER' },
            { code: 'COMMISSION_REFUND_SELLER' },
            { code: 'TRANSACTION_FEE_REFUND_SELLER' },
            { code: 'PRODUCT_COST_REFUND_SELLER' },
            { code: 'SHIPPING_FEE_REFUND_SELLER' },
            { code: 'OTHER_REFUND_SELLER' },
            { code: 'OTHER_REFUND_BUYER' }
        ];

        const data = reactive({
            mappedCsv: null,
            fields: RefundHeaderHelper.getImportFields(),
            previewHeaders: RefundHeaderHelper.getImportHeaders(),
            headers: [...ImportHeaderHelper.getHeaders(), ...RefundHeaderHelper.getImportHeaders()]
        });

        onMounted(async () => {
            // Load master data for reference button
            await loadAllMasterData();
        });

        return {
            data,
            marketplaceOptions,
            currencyOptions,
            refundTypeList
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListRefund: PathDefinition.listRefund,
            formLabelText: RefundLabelTextHelper.getLabelText
        };
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            return ImportValidationHelper.validateRows(mappedCsv, RefundImportValidation, importHeaderValues);
        },

        groupDataByOrderKey(mappedCsv) {
            const orderFields = ['orderNumber', 'marketplaceCode'];
            const refundItemFields = [
                'refundType',
                'refundAmount',
                'refundCurrencyCode',
                'dateRefundOn',
                'description',
                'reason',
                'remarks'
            ];

            // Create composite key: orderNumber + marketplaceCode
            const groupByCompositeKey = (item) => `${item.orderNumber}|${item.marketplaceCode}`;

            return _.mapValues(_.groupBy(mappedCsv, groupByCompositeKey), (group) => {
                const orderInfo = _.pick(group[0], [...orderFields, 'status', 'error']);
                const refundItems = group.map((item) => _.pick(item, [...refundItemFields, 'status', 'error']));

                return {
                    orderInfo,
                    refundItems,
                    orderNumber: group[0].orderNumber,
                    marketplaceCode: group[0].marketplaceCode,
                    compositeKey: groupByCompositeKey(group[0])
                };
            });
        },

        validateGroupConsistency(groupedData) {
            const orderFields = ['orderNumber', 'marketplaceCode'];

            for (const compositeKey in groupedData) {
                const group = groupedData[compositeKey];
                const originalData = this.data.mappedCsv.filter(
                    (item) => item.orderNumber === group.orderNumber && item.marketplaceCode === group.marketplaceCode
                );

                // Check if orderNumber and marketplaceCode are consistent across rows
                for (let i = 1; i < originalData.length; i++) {
                    for (const field of orderFields) {
                        if (originalData[0][field] !== originalData[i][field]) {
                            originalData[i].status = ImportTableLabelTextHelper.getImportResultValue.error;
                            originalData[i].error = `Inconsistent ${field} for order group`;
                        }
                    }
                }
            }
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;

            // Group data by orderNumber + marketplaceCode and validate consistency
            const groupedData = this.groupDataByOrderKey(this.data.mappedCsv);
            this.validateGroupConsistency(groupedData);

            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                // Process each order group
                for (const compositeKey in groupedData) {
                    await this.createRefundGroup(groupedData[compositeKey]);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            if (item.status === ImportTableLabelTextHelper.getImportResultValue.skipped) {
                return 'row-validation-skipped';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, RefundImportValidation);
        },
        async createRefundGroup(refundGroup) {
            const { orderNumber, marketplaceCode } = refundGroup;

            try {
                const groupRows = this.data.mappedCsv.filter(
                    (item) => item.orderNumber === orderNumber && item.marketplaceCode === marketplaceCode
                );

                const refundItems = groupRows.map((item) => {
                    if (!item.importRowId) {
                        item.importRowId = crypto.randomUUID();
                    }

                    return {
                        importRowId: item.importRowId,
                        refundType: item.refundType,
                        refundAmount: parseFloat(item.refundAmount),
                        refundCurrencyCode: item.refundCurrencyCode,
                        dateRefundOn: item.dateRefundOn,
                        description: item.description,
                        reason: item.reason || '',
                        remarks: item.remarks || ''
                    };
                });

                const request = {
                    orderNumber: orderNumber,
                    marketplaceCode: marketplaceCode,
                    refundItems
                };

                const requestUrl = `/api/staff/refund/create`;
                const response = await fetchWrapper.post(requestUrl, request);

                if (response.success) {
                    const skippedByRowId = new Map(
                        (response.result?.skipped || []).map((skippedItem) => [
                            skippedItem.importRowId,
                            skippedItem.reason
                        ])
                    );

                    for (const row of groupRows) {
                        if (skippedByRowId.has(row.importRowId)) {
                            row.status = ImportTableLabelTextHelper.getImportResultValue.skipped;
                            row.error = skippedByRowId.get(row.importRowId);
                        } else {
                            row.status = ImportTableLabelTextHelper.getImportResultValue.success;
                            row.error = null;
                        }
                    }
                } else {
                    this.updateRefundRowsStatus(
                        orderNumber,
                        marketplaceCode,
                        ImportTableLabelTextHelper.getImportResultValue.error,
                        response.errors
                    );
                }
            } catch (error) {
                console.error('Error creating refund group:', error);
                this.updateRefundRowsStatus(
                    orderNumber,
                    marketplaceCode,
                    ImportTableLabelTextHelper.getImportResultValue.error,
                    error.message || 'Unknown error'
                );
            }
        },

        updateRefundRowsStatus(orderNumber, marketplaceCode, status, error) {
            for (let i = 0; i < this.data.mappedCsv.length; i++) {
                if (
                    this.data.mappedCsv[i].orderNumber === orderNumber &&
                    this.data.mappedCsv[i].marketplaceCode === marketplaceCode
                ) {
                    this.data.mappedCsv[i].status = status;
                    if (error) {
                        this.data.mappedCsv[i].error = error;
                    }
                }
            }
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}

.row-validation-skipped {
    background: #fff3cd;
}
</style>
